import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  ArrowLeft,
  Building2,
  Users,
  Clock,
  Shield,
  Eye,
  Edit,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Info,
  LogIn,
  Send,
  MessageSquare,
  CheckSquare,
  Circle,
} from "lucide-react";
import { useGaContext } from "@/hooks/useGaContext";
import { enterBrokerPortal } from "@/hooks/useGAServiceSession";

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-1">{children}</p>;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground font-medium text-right">{value || "—"}</span>
    </div>
  );
}

function SignalBar({ label, earned, max, detail }: { label: string; earned: number; max: number; detail?: string }) {
  const pct = max > 0 ? (earned / max) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-foreground">{label}</span>
        <span className="font-semibold">{earned}/{max} pts</span>
      </div>
      <div className="w-full bg-muted rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full ${pct >= 66 ? "bg-green-500" : pct >= 33 ? "bg-amber-400" : "bg-red-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
}

const SETUP_STEPS = [
  { key: "license_assigned", label: "License Assigned", description: "GA assigned a license to this broker" },
  { key: "first_login", label: "First Login", description: "Broker logged into Administr at least once" },
  { key: "first_employer", label: "First Employer Added", description: "At least one employer group onboarded" },
  { key: "first_census", label: "First Census", description: "At least one employee record added" },
  { key: "first_enrollment", label: "First Enrollment", description: "At least one employee enrolled in benefits" },
];

function SetupTracker({ progress }: { progress: Record<string, boolean> }) {
  const completed = SETUP_STEPS.filter((s) => progress[s.key]).length;
  const pct = Math.round((completed / SETUP_STEPS.length) * 100);

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
          <CheckSquare className="w-4 h-4" /> Setup Progress
        </CardTitle>
        <span className="text-xs font-semibold text-muted-foreground">{completed}/{SETUP_STEPS.length} steps</span>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              pct === 100 ? "bg-green-500" : pct >= 60 ? "bg-blue-500" : "bg-amber-400"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="space-y-2 pt-1">
          {SETUP_STEPS.map((step, idx) => {
            const done = !!progress[step.key];
            const prior = idx === 0 || !!progress[SETUP_STEPS[idx - 1].key];
            return (
              <div key={step.key} className="flex items-start gap-3">
                <div className={`mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                  done ? "bg-green-100 dark:bg-green-900" : prior ? "bg-amber-50 dark:bg-amber-950 border border-amber-300 dark:border-amber-700" : "bg-muted"
                }`}>
                  {done ? (
                    <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" />
                  ) : (
                    <Circle className={`w-3 h-3 ${prior ? "text-amber-400 dark:text-amber-500" : "text-muted-foreground"}`} />
                  )}
                </div>
                <div>
                  <p className={`text-sm font-medium ${done ? "text-foreground" : "text-muted-foreground"}`}>{step.label}</p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function GABrokerDetail() {
  const { gaId } = useGaContext();
  const { brokerAccountId } = useParams<{ brokerAccountId: string }>();
  const { toast } = useToast();

  const [outreach, setOutreach] = useState({ subject: "", message: "" });

  const { data: gaProfile } = useQuery<any>({
    queryKey: ["/api/ga", gaId, "profile"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/profile`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!gaId,
  });

  const { data: link, isLoading } = useQuery<any>({
    queryKey: ["/api/ga", gaId, "broker-links", brokerAccountId],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/broker-links/${brokerAccountId}`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!gaId && !!brokerAccountId,
  });

  const { data: clients = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "broker-links", brokerAccountId, "clients"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/broker-links/${brokerAccountId}/clients`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId && !!brokerAccountId,
  });

  const { data: auditLog = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "audit-log", { broker: brokerAccountId }],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/audit-log?broker_account_id=${brokerAccountId}&limit=30`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId && !!brokerAccountId,
  });

  const { data: scoreBreakdown } = useQuery<any>({
    queryKey: ["/api/ga", gaId, "analytics", "brokers", brokerAccountId, "score-breakdown"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/analytics/brokers/${brokerAccountId}/score-breakdown`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!gaId && !!brokerAccountId,
  });

  const { data: setupProgress } = useQuery<Record<string, boolean>>({
    queryKey: ["/api/ga", gaId, "broker-links", brokerAccountId, "setup-progress"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/broker-links/${brokerAccountId}/setup-progress`);
      if (!res.ok) return {};
      return res.json();
    },
    enabled: !!gaId && !!brokerAccountId,
  });

  const { data: outreachHistory = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "broker-links", brokerAccountId, "outreach"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/broker-links/${brokerAccountId}/outreach`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId && !!brokerAccountId,
  });

  const outreachMutation = useMutation({
    mutationFn: async (data: { subject: string; message: string }) =>
      apiRequest("POST", `/api/ga/${gaId}/broker-links/${brokerAccountId}/outreach`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "broker-links", brokerAccountId, "outreach"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "audit-log", { broker: brokerAccountId }] });
      toast({ title: "Message sent", description: "Your outreach has been logged and recorded." });
      setOutreach({ subject: "", message: "" });
    },
    onError: () =>
      toast({ title: "Failed to send", description: "Please try again.", variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!link) {
    return (
      <div className="text-center py-24 text-muted-foreground">
        <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>Broker account not found or not linked to your GA.</p>
      </div>
    );
  }

  const snap = link.snapshot;
  const buildAccess = link.buildAccess;
  const score = snap?.adoptionScore ?? 0;
  const band = snap?.adoptionBand ?? "building";

  const bandColor = band === "power_user" ? "text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-950" :
    band === "active" ? "text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-950" : "text-muted-foreground bg-muted";
  const bandLabel = band === "power_user" ? "Power User" : band === "active" ? "Active" : "Building";

  const outreachLogs = outreachHistory;
  const generalAudit = auditLog.filter((e: any) => e.action !== "outreach");

  return (
    <div className="space-y-6">
      {/* Back */}
      <Link href="/ga/brokers">
        <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2">
          <ArrowLeft className="w-4 h-4 mr-1" /> Broker Accounts
        </Button>
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{link.brokerAgencyName || "Broker Account"}</h1>
          <div className="flex items-center gap-2 mt-1">
            {link.brokerNpn && <span className="text-xs text-muted-foreground">NPN: {link.brokerNpn}</span>}
            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
              link.status === "active" ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300" :
              link.status === "pending" ? "bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300" :
              "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300"
            }`}>
              {link.status === "pending" ? "Invited" : link.status === "active" ? "Active" : "Revoked"}
            </span>
            <span className={`text-xs ${link.gaFunded ? "text-primary" : "text-muted-foreground"}`}>
              {link.gaFunded ? "GA-funded" : "Self-funded"}
            </span>
          </div>
        </div>

        {/* Enter Broker Portal */}
        <Button
          onClick={() =>
            enterBrokerPortal(
              {
                gaId,
                gaName: gaProfile?.name || "GA Portal",
                brokerAccountId: brokerAccountId!,
                brokerAgencyName: link.brokerAgencyName || "Broker",
                scope: buildAccess?.status === "active" ? "assist" : "view_only",
              },
              brokerAccountId!
            )
          }
          className="shrink-0 bg-[#1c35a7] hover:bg-[#162a8a] text-white flex items-center gap-2"
        >
          <LogIn className="w-4 h-4" />
          Enter Broker Portal
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="bg-muted p-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="outreach">
            Outreach
            {outreachLogs.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-[10px] font-semibold">
                {outreachLogs.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="clients">Shared Clients</TabsTrigger>
          <TabsTrigger value="build-access">Build Access</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-muted-foreground">Account Details</CardTitle></CardHeader>
              <CardContent>
                <InfoRow label="Agency Name" value={link.brokerAgencyName} />
                <InfoRow label="Contact Email" value={link.brokerContactEmail} />
                <InfoRow label="NPN" value={link.brokerNpn} />
                <InfoRow label="Linked Since" value={link.linkedAt ? new Date(link.linkedAt).toLocaleDateString() : null} />
                <InfoRow label="Funding" value={link.gaFunded ? "GA-funded" : "Self-funded"} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-muted-foreground">Usage Metrics</CardTitle></CardHeader>
              <CardContent>
                <InfoRow label="Employers Onboarded" value={snap?.employersTotal ?? "—"} />
                <InfoRow label="Employees" value={snap?.employeesTotal ?? "—"} />
                <InfoRow label="Enrolled Employees" value={snap?.employeesEnrolled ?? "—"} />
                <InfoRow label="Enrollment %" value={snap?.enrollmentCompletionPct ? `${snap.enrollmentCompletionPct}%` : "—"} />
                <InfoRow label="Last Login" value={snap?.lastLoginAt ? new Date(snap.lastLoginAt).toLocaleDateString() : "Never"} />
              </CardContent>
            </Card>
          </div>
          {setupProgress && (
            <SetupTracker progress={setupProgress} />
          )}
        </TabsContent>

        {/* Outreach */}
        <TabsContent value="outreach" className="mt-4 space-y-4">
          {/* Compose */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Compose Message
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label>Subject</Label>
                <Input
                  placeholder="e.g. Quarterly check-in, New resources available…"
                  value={outreach.subject}
                  onChange={(e) => setOutreach((o) => ({ ...o, subject: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Message</Label>
                <Textarea
                  placeholder="Write your message to this broker…"
                  rows={5}
                  value={outreach.message}
                  onChange={(e) => setOutreach((o) => ({ ...o, message: e.target.value }))}
                />
              </div>
              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-muted-foreground">
                  Sent to <strong>{link.brokerContactEmail || link.brokerAgencyName}</strong> · logged to audit trail
                </p>
                <Button
                  onClick={() => {
                    if (!outreach.subject.trim() || !outreach.message.trim()) {
                      toast({ title: "Subject and message are required", variant: "destructive" });
                      return;
                    }
                    outreachMutation.mutate(outreach);
                  }}
                  disabled={outreachMutation.isPending}
                  className="gap-2"
                >
                  <Send className="w-4 h-4" />
                  {outreachMutation.isPending ? "Sending…" : "Send Message"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* History */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground">Message History</CardTitle>
            </CardHeader>
            <CardContent>
              {outreachLogs.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No messages sent yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {outreachLogs.map((entry: any) => (
                    <div key={entry.id} className="rounded-lg border border-border bg-muted p-4">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="text-sm font-semibold text-foreground">{entry.metadata?.subject || "No subject"}</p>
                        <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                          {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ""}
                        </span>
                      </div>
                      <p className="text-sm text-foreground whitespace-pre-line">{entry.metadata?.message}</p>
                      {entry.metadata?.senderEmail && (
                        <p className="text-xs text-muted-foreground mt-2">From: {entry.metadata.senderEmail}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shared Clients */}
        <TabsContent value="clients" className="mt-4">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-muted-foreground">Clients Shared by Broker</CardTitle>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Info className="w-3 h-3" />
                Broker controls which clients are shared
              </div>
            </CardHeader>
            <CardContent>
              {clients.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Eye className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No clients shared yet</p>
                  <p className="text-xs mt-1">The broker must share clients from their settings</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left pb-2">Client Name</th>
                      <th className="text-left pb-2">Scope</th>
                      <th className="text-left pb-2">Shared Since</th>
                      <th className="pb-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {clients.map((c: any) => (
                      <tr key={c.id}>
                        <td className="py-2.5 font-medium">{c.clientName || c.clientId}</td>
                        <td className="py-2.5">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            c.scope === "assist" ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300" : "bg-muted text-muted-foreground"
                          }`}>
                            {c.scope === "assist" ? "Assist" : "View Only"}
                          </span>
                        </td>
                        <td className="py-2.5 text-muted-foreground text-xs">
                          {c.sharedAt ? new Date(c.sharedAt).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-2.5 text-right">
                          <Link href={`/ga/brokers/${brokerAccountId}/clients/${c.clientId}`}>
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-primary">
                              {c.scope === "assist" ? <><Edit className="w-3 h-3 mr-1" /> Assist</> : <><Eye className="w-3 h-3 mr-1" /> View</>}
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Build Access */}
        <TabsContent value="build-access" className="mt-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold text-muted-foreground">Build Access</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {!buildAccess || buildAccess.status !== "active" ? (
                <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 p-4 flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Build access not granted</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Ask <strong>{link.brokerAgencyName}</strong> to grant build access from their Settings → Partner Access page.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 p-4 flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800 dark:text-green-300">Build access active</p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      Granted {buildAccess.grantedAt ? new Date(buildAccess.grantedAt).toLocaleDateString() : ""}
                      {buildAccess.expiresAt ? ` · Expires ${new Date(buildAccess.expiresAt).toLocaleDateString()}` : " · No expiry set"}
                    </p>
                  </div>
                </div>
              )}
              {buildAccess?.status === "expired" && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                  Build access expired on {new Date(buildAccess.expiresAt).toLocaleDateString()}.
                  Ask the broker to renew from their settings.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance */}
        <TabsContent value="performance" className="space-y-4 mt-4">
          <Card>
            <CardContent className="pt-5">
              <div className="flex items-center gap-4 mb-6">
                <div className={`rounded-xl p-4 ${bandColor}`}>
                  <p className="text-5xl font-bold">{score}</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-foreground">{bandLabel}</p>
                  <p className="text-sm text-muted-foreground">Adoption band</p>
                </div>
              </div>
              <div className="space-y-4">
                <p className="text-sm font-semibold text-foreground">Score Breakdown</p>
                {scoreBreakdown ? (
                  <div className="space-y-3">
                    <SignalBar label="Login frequency" earned={scoreBreakdown.loginScore || 0} max={15} detail={`${snap?.loginsLast30d || 0} logins in last 30 days`} />
                    <SignalBar label="Employers onboarded" earned={scoreBreakdown.employerScore || 0} max={20} detail={`${snap?.employersTotal || 0} employers`} />
                    <SignalBar label="Census completeness" earned={scoreBreakdown.censusScore || 0} max={15} detail={`${snap?.censusCopletednessPct || 0}% complete`} />
                    <SignalBar label="Enrollment completion" earned={scoreBreakdown.enrollmentScore || 0} max={20} detail={`${snap?.enrollmentCompletionPct || 0}% enrolled`} />
                    <SignalBar label="Quoting activity" earned={scoreBreakdown.quotingScore || 0} max={10} detail={`${snap?.quotesRunLast30d || 0} quotes in last 30 days`} />
                    <SignalBar label="ACA / compliance features" earned={scoreBreakdown.acaScore || 0} max={10} />
                    <SignalBar label="HRIS / HR features" earned={scoreBreakdown.hrisScore || 0} max={5} />
                    <SignalBar label="Carrier connections" earned={scoreBreakdown.carrierScore || 0} max={5} detail={`${snap?.carrierConnectionsCount || 0} active connections`} />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <SignalBar label="Login frequency" earned={0} max={15} />
                    <SignalBar label="Employers onboarded" earned={0} max={20} />
                    <SignalBar label="Census completeness" earned={0} max={15} />
                    <SignalBar label="Enrollment completion" earned={0} max={20} />
                    <SignalBar label="Quoting activity" earned={0} max={10} />
                    <SignalBar label="ACA / compliance features" earned={0} max={10} />
                    <SignalBar label="HRIS / HR features" earned={0} max={5} />
                    <SignalBar label="Carrier connections" earned={0} max={5} />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log */}
        <TabsContent value="audit" className="mt-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold text-muted-foreground">Audit Log — This Broker</CardTitle></CardHeader>
            <CardContent>
              {generalAudit.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Shield className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No activity logged for this broker</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {generalAudit.map((entry: any) => (
                    <div key={entry.id} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                      <span className="text-xs text-muted-foreground whitespace-nowrap mt-0.5">
                        {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ""}
                      </span>
                      <div>
                        <p className="text-sm text-foreground">{entry.action}</p>
                        {entry.recordType && (
                          <p className="text-xs text-muted-foreground">{entry.recordType} · {entry.recordId}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
